/**
 * Custom hook for fetching workspace data (members, permissions, membership ensuring)
 * Extracted from Activity.tsx to improve code organization
 */

import { useEffect, useState, useRef } from "react";
import { auth } from "../../../firebase";
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
  Unsubscribe,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { WorkspacePermissions, WorkspaceRole, WorkspaceSettings } from "../../../types";
import { isDemoMode } from "../../../utils/demoMode";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";
import { validateUserDocumentExists } from "../../../utils/memberUtils";

export interface WorkspaceMember {
  id: string;
  name?: string | undefined;
  email?: string | undefined;
  role?: string | undefined; // Role from members subcollection (owner, admin, user, viewer)
  photoURL?: string | undefined; // User profile photo URL
}

export interface UseWorkspaceDataResult {
  workspaceMembers: WorkspaceMember[];
  membersLoaded: boolean;
  membershipEnsured: boolean;
  permissions: WorkspacePermissions | null;
  currentUserRole: WorkspaceRole;
  workspaceOwnerId: string | null;
  workspaceCreatedAt: Date | null;
}

export interface UseWorkspaceDataParams {
  workspaceId: string;
  initialFilters?: {
    users?: string[];
  };
  onMembersLoaded?: (members: WorkspaceMember[]) => void;
}

// Cache for workspace data per workspace - persists across component unmounts
const workspaceDataCache = new Map<string, {
  workspaceMembers: WorkspaceMember[];
  membershipEnsured: boolean;
  permissions: WorkspacePermissions | null;
  currentUserRole: WorkspaceRole;
  workspaceOwnerId: string | null;
  timestamp: number;
}>();

// Cache expiration time: 5 minutes
const CACHE_EXPIRY_MS = 5 * 60 * 1000;

/**
 * Hook for ensuring membership, fetching permissions, and fetching workspace members
 * Uses caching to avoid reloading data when navigating back to Activity
 */
export const useWorkspaceData = ({
  workspaceId,
  initialFilters,
  onMembersLoaded,
}: UseWorkspaceDataParams): UseWorkspaceDataResult => {
  // Check cache first
  const cachedData = workspaceDataCache.get(workspaceId);
  const hasValidCache = !!(cachedData && (Date.now() - cachedData.timestamp) < CACHE_EXPIRY_MS);
  
  // Seed with current user when no cache so creator avatar can show on first paint (e.g. iPhone after creating transaction)
  const [workspaceMembers, setWorkspaceMembers] = useState<WorkspaceMember[]>(() => {
    if (hasValidCache && cachedData) return cachedData.workspaceMembers;
    const currentUser = auth.currentUser;
    if (currentUser)
      return [{
        id: currentUser.uid,
        name: currentUser.displayName || undefined,
        email: currentUser.email || undefined,
        photoURL: currentUser.photoURL || undefined,
      }];
    return [];
  });
  const [membersLoaded, setMembersLoaded] = useState<boolean>(hasValidCache);
  const [membershipEnsured, setMembershipEnsured] = useState<boolean>(
    hasValidCache && cachedData ? cachedData.membershipEnsured : false
  );
  const [permissions, setPermissions] = useState<WorkspacePermissions | null>(
    hasValidCache && cachedData ? cachedData.permissions : null
  );
  const [currentUserRole, setCurrentUserRole] = useState<WorkspaceRole>(
    hasValidCache && cachedData ? cachedData.currentUserRole : "user"
  );
  const [workspaceOwnerId, setWorkspaceOwnerId] = useState<string | null>(
    hasValidCache && cachedData ? cachedData.workspaceOwnerId : null
  );
  const [workspaceCreatedAt, setWorkspaceCreatedAt] = useState<Date | null>(null);

  // Ensure user is in workspace members subcollection (for Storage rules and Firestore permissions)
  // This ensures all users (owner, admin, user, viewer) can access transactions
  useEffect(() => {
    if (!workspaceId || !auth.currentUser) {
      // In demo mode or bypass auth mode, allow loading without membership check
      if (isDemoMode() || isBypassAuthEnabled()) {
        setMembershipEnsured(true);
        // Update cache
        const currentCache = workspaceDataCache.get(workspaceId);
        if (currentCache) {
          workspaceDataCache.set(workspaceId, {
            ...currentCache,
            membershipEnsured: true,
          });
        } else {
          workspaceDataCache.set(workspaceId, {
            workspaceMembers: [],
            membershipEnsured: true,
            permissions: null,
            currentUserRole: "user",
            workspaceOwnerId: null,
            timestamp: Date.now(),
          });
        }
      }
      return;
    }

    const ensureMembership = async (): Promise<void> => {
      try {
        const userId = auth.currentUser!.uid;
        
        // In bypass auth mode, skip trying to add the anonymous user to members
        // The "bypass-user" member should already exist (created by backend script)
        // The anonymous user ID is just for Firestore writes, not for membership
        if (isBypassAuthEnabled()) {
          const workspaceRef = doc(db, "workspaces", workspaceId);
          const workspaceSnap = await getDoc(workspaceRef);
          
          if (workspaceSnap.exists()) {
            const workspaceData = workspaceSnap.data();
            const isOwner = workspaceData?.ownerId === userId;
            
            // In bypass mode, if this is the owner (anonymous user), skip membership creation
            if (isOwner) {
              // In bypass mode, the anonymous user is the owner but we don't add it to members
              // Instead, "bypass-user" is the member that represents the developer
              // Just ensure membership is set to true to allow queries
              setMembershipEnsured(true);
              return;
            }
          } else {
            // Workspace doesn't exist, but in bypass mode we can still proceed
            setMembershipEnsured(true);
            return;
          }
        }
        
        const workspaceRef = doc(db, "workspaces", workspaceId);
        const workspaceSnap = await getDoc(workspaceRef);
        
        if (!workspaceSnap.exists()) {
          console.error("Workspace not found:", workspaceId);
          setMembershipEnsured(true); // Allow query to proceed, will fail gracefully
          return;
        }

        const workspaceData = workspaceSnap.data();
        const isOwner = workspaceData?.ownerId === userId;
        
        // Check if user is already in members subcollection
        const memberRef = doc(db, "workspaces", workspaceId, "members", userId);
        const memberSnap = await getDoc(memberRef);
        
        if (!memberSnap.exists()) {
          // In bypass auth mode with owner, skip validation (already handled above)
          // For non-owners or non-bypass mode, validate user document exists before creating member
          if (!isBypassAuthEnabled() || !isOwner) {
            // Validate user document exists before creating member (prevents orphaned members)
            // Use Promise.race to timeout validation after 1 second to prevent blocking
            let userDocumentValid = false;
            let validationTimedOut = false;
            
            try {
              const validationPromise = validateUserDocumentExists(userId);
              const timeoutPromise = new Promise<{ timedOut: boolean }>((resolve) => 
                setTimeout(() => resolve({ timedOut: true }), 1000) // Timeout after 1 second
              );
              
              // Race between validation and timeout
              const result = await Promise.race([
                validationPromise.then(valid => ({ valid, timedOut: false })),
                timeoutPromise.then(({ timedOut }) => ({ valid: false, timedOut }))
              ]);
              
              userDocumentValid = result.valid;
              validationTimedOut = result.timedOut;
            } catch (error) {
              console.warn(`Error validating user document: ${error}`);
              validationTimedOut = true; // Treat errors as timeout to proceed
            }
            
            if (!userDocumentValid && !isOwner && !validationTimedOut) {
              // Only block non-owners if validation fails and we didn't timeout
              // Owners can proceed even without user document (Firestore rules allow it)
              
              // Skip warning for orphaned user IDs (starting with "Ao7fhBrt") - these are already filtered out in UI
              // Also skip warning in bypass auth mode (anonymous users don't have user documents)
              const isOrphanedUserId = userId.startsWith("Ao7fhBrt");
              const isBypassMode = isBypassAuthEnabled();
              if (!isOrphanedUserId && !isBypassMode) {
                console.warn(`⚠️ Cannot add user ${userId} to members: user document doesn't exist or has no data`);
              }
              // Still set membershipEnsured to true to allow queries to proceed
              // The query will fail gracefully if user doesn't have permission
              setMembershipEnsured(true);
              return;
            }
            
            // If validation timed out, log a warning but proceed anyway
            if (validationTimedOut) {
              console.warn(`⚠️ User document validation timed out, proceeding with membership creation anyway`);
            }
          }
          
          // Add user to members subcollection (owner or regular member)
          // Firestore rules allow users to add themselves
          // This ensures all roles (owner, admin, user, viewer) are properly tracked
          await setDoc(memberRef, {
            uid: userId,
            role: isOwner ? "owner" : "user",
            joinedAt: serverTimestamp(),
            email: auth.currentUser?.email || "",
            displayName: auth.currentUser?.displayName || "",
          });
          // User added to workspace members subcollection
        } else {
          // User is already a member, but ensure role is correct if they're the owner
          const memberData = memberSnap.data();
          if (isOwner && memberData?.role !== "owner") {
            // Update role to owner if user is the owner but role is incorrect
            await updateDoc(memberRef, {
              role: "owner",
            });
          }
          // User is already in workspace members
        }
        // Membership ensured
        setMembershipEnsured(true);
        // Update cache
        const currentCache = workspaceDataCache.get(workspaceId);
        if (currentCache) {
          workspaceDataCache.set(workspaceId, {
            ...currentCache,
            membershipEnsured: true,
          });
        }
      } catch (error) {
        console.error("Error ensuring workspace membership:", error);
        // Still set to true to allow queries to proceed (they might work if user is owner)
        // Firestore rules allow owners to access even without member document
        setMembershipEnsured(true);
        // Update cache
        const currentCache = workspaceDataCache.get(workspaceId);
        if (currentCache) {
          workspaceDataCache.set(workspaceId, {
            ...currentCache,
            membershipEnsured: true,
          });
        }
      }
    };

    // Ensure membership first, then it will be ready for queries
    ensureMembership();
  }, [workspaceId]);

  // Load permissions and determine user role
  useEffect(() => {
    if (!workspaceId || !auth.currentUser) return;

    const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
    const unsubscribeSettings = onSnapshot(settingsRef, (docSnap) => {
      let newPermissions: WorkspacePermissions;
      if (docSnap.exists()) {
        const data = docSnap.data() as WorkspaceSettings;
        if (data.permissions) {
          newPermissions = data.permissions;
        } else {
          // Default permissions
          newPermissions = {
            manageWorkspaceConfig: ["owner", "admin"],
            archiveAccounts: ["owner"],
            deleteTransactions: ["owner"],
            editTransactions: ["owner", "admin"],
            viewTransactionHistory: ["owner", "admin"],
            editPreferences: ["owner", "admin"],
            addCurrency: ["owner", "admin"],
            changeDefaultCurrency: ["owner", "admin"],
            changeCurrencyRateDecimalPlaces: ["owner", "admin"],
          };
        }
      } else {
        // Default permissions
        newPermissions = {
          manageWorkspaceConfig: ["owner", "admin"],
          archiveAccounts: ["owner"],
          deleteTransactions: ["owner"],
          editTransactions: ["owner", "admin"],
          viewTransactionHistory: ["owner", "admin"],
          editPreferences: ["owner", "admin"],
          addCurrency: ["owner", "admin"],
          changeDefaultCurrency: ["owner", "admin"],
          changeCurrencyRateDecimalPlaces: ["owner", "admin"],
        };
      }
      setPermissions(newPermissions);
      // Update cache
      const currentCache = workspaceDataCache.get(workspaceId);
      if (currentCache) {
        workspaceDataCache.set(workspaceId, {
          ...currentCache,
          permissions: newPermissions,
        });
      }
    });

    // Get workspace owner
    const workspaceRef = doc(db, "workspaces", workspaceId);
    const unsubscribeWorkspace = onSnapshot(workspaceRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        const ownerId = data.ownerId as string;
        setWorkspaceOwnerId(ownerId);
        
        // Determine current user role
        const userId = auth.currentUser?.uid;
        let newRole: WorkspaceRole = "user";
        if (userId && userId === ownerId) {
          // User is primary owner - always set role to "owner"
          newRole = "owner";
          setCurrentUserRole("owner");
        } else if (userId) {
          // Check member role (only if not primary owner)
          const memberRef = doc(db, "workspaces", workspaceId, "members", userId);
          getDoc(memberRef).then((memberSnap) => {
            let role: WorkspaceRole = "user";
            if (memberSnap.exists()) {
              const memberData = memberSnap.data();
              const memberRole = memberData.role as string;
              // Map "member" role to "user" for consistency
              if (memberRole === "owner") {
                role = "owner";
              } else if (memberRole === "admin") {
                role = "admin";
              } else {
                role = "user";
              }
            }
            setCurrentUserRole(role);
            // Update cache
            const currentCache = workspaceDataCache.get(workspaceId);
            if (currentCache) {
              workspaceDataCache.set(workspaceId, {
                ...currentCache,
                currentUserRole: role,
                workspaceOwnerId: ownerId,
              });
            }
          });
        }
        // Update cache immediately for owner case
        const currentCache = workspaceDataCache.get(workspaceId);
        if (currentCache) {
          workspaceDataCache.set(workspaceId, {
            ...currentCache,
            workspaceOwnerId: ownerId,
            currentUserRole: newRole,
          });
        }
      }
    });

    return () => {
      unsubscribeSettings();
      unsubscribeWorkspace();
    };
  }, [workspaceId]);

  // Fetch workspace members and their user details - initialize with current user immediately for UI
  // Use onSnapshot for real-time updates when members are added/removed
  useEffect(() => {
    if (!workspaceId) return;
    const currentUser = auth.currentUser;
    
    // In demo mode, skip user check and just mark as loaded
    if (isDemoMode()) {
      const emptyMembers: WorkspaceMember[] = [];
      setWorkspaceMembers(emptyMembers);
      setMembersLoaded(true);
      // Update cache
      workspaceDataCache.set(workspaceId, {
        workspaceMembers: emptyMembers,
        membershipEnsured: true,
        permissions: null,
        currentUserRole: "user",
        workspaceOwnerId: null,
        timestamp: Date.now(),
      });
      return;
    }
    
    // In bypass auth mode, load workspace owner so their name can be displayed
    // Only include bypass-user if it exists as a real member in Firebase
    // Don't add demo-user - it doesn't exist and shouldn't appear in the filter
    // Note: dev-user-developer is replaced with bypass-user
    if (isBypassAuthEnabled()) {
      const loadWorkspaceOwner = async () => {
        try {
          const workspaceRef = doc(db, "workspaces", workspaceId);
          const workspaceSnap = await getDoc(workspaceRef);
          
          const members: WorkspaceMember[] = [];
          
          if (workspaceSnap.exists()) {
            const workspaceData = workspaceSnap.data();
            const ownerId = workspaceData.ownerId as string | undefined;
            
            // IMPORTANT: Set workspaceOwnerId so it can be used by cache fetch and other components
            // This ensures the owner's info is fetched from the users collection even if we can't access it here
            if (ownerId) {
              setWorkspaceOwnerId(ownerId);
              // Update cache with ownerId
              const currentCache = workspaceDataCache.get(workspaceId);
              if (currentCache) {
                workspaceDataCache.set(workspaceId, {
                  ...currentCache,
                  workspaceOwnerId: ownerId,
                });
              } else {
                workspaceDataCache.set(workspaceId, {
                  workspaceMembers: [],
                  membershipEnsured: true,
                  permissions: null,
                  currentUserRole: "user",
                  workspaceOwnerId: ownerId,
                  timestamp: Date.now(),
                });
              }
              if (process.env.NODE_ENV === "development") {
                console.log(`[Activity] Set workspaceOwnerId in bypass mode: ${ownerId}`);
              }
            }
            
            if (ownerId) {
              // In bypass auth mode, we may not have permission to read user documents
              // So we'll try to get owner info from the members subcollection first (which we can access)
              let ownerName: string | undefined;
              let ownerEmail: string | undefined;
              
              // Try to get owner info from members subcollection (accessible in bypass auth mode)
              try {
                const ownerMemberDoc = await getDoc(doc(db, "workspaces", workspaceId, "members", ownerId));
                if (ownerMemberDoc.exists()) {
                  const ownerMemberData = ownerMemberDoc.data();
                  ownerName = (ownerMemberData.name as string | undefined) || (ownerMemberData.displayName as string | undefined);
                  ownerEmail = ownerMemberData.email as string | undefined;
                }
              } catch (error) {
                // Member document might not exist - that's OK, we'll try user document
              }
              
              // Try to get owner info from user document (may fail due to permissions in bypass auth mode)
              let ownerPhotoURL: string | undefined;
              try {
                const ownerUserDoc = await getDoc(doc(db, "users", ownerId));
                if (ownerUserDoc.exists()) {
                  const ownerUserData = ownerUserDoc.data();
                  // Use user document data if member document didn't have it
                  if (!ownerName) {
                    ownerName = (ownerUserData.name as string | undefined) || (ownerUserData.displayName as string | undefined);
                  }
                  if (!ownerEmail) {
                    ownerEmail = ownerUserData.email as string | undefined;
                  }
                  ownerPhotoURL = ownerUserData.photoURL as string | undefined;
                }
              } catch (error: any) {
                // Permission errors are expected in bypass auth mode - silently ignore them
                // We already have name/email from member document if available
                const isPermissionError = error?.code === "permission-denied" || 
                                         error?.message?.includes("permission") ||
                                         error?.message?.includes("Missing or insufficient permissions");
                // Don't log permission errors - they're expected in bypass auth mode
                if (!isPermissionError) {
                  // Only log unexpected errors
                  console.error("Error fetching workspace owner user doc:", error);
                }
              }
              
              // Create owner member with whatever info we have
              const ownerMember: WorkspaceMember = {
                id: ownerId,
                name: ownerName,
                email: ownerEmail,
                role: "owner",
                photoURL: ownerPhotoURL,
              };
              members.push(ownerMember);
            }
          }
          
          // Only add bypass-user if it exists as a real member in Firebase
          // Don't add demo-user - it doesn't exist and shouldn't appear in the filter
          // Note: dev-user-developer is replaced with bypass-user
          // Check if bypass-user exists in members subcollection
          try {
            const bypassMemberDoc = await getDoc(doc(db, "workspaces", workspaceId, "members", "bypass-user"));
            if (bypassMemberDoc.exists()) {
              const bypassMemberData = bypassMemberDoc.data();
              if (!members.find(m => m.id === "bypass-user")) {
                members.push({
                  id: "bypass-user",
                  name: bypassMemberData.name as string | undefined,
                  email: bypassMemberData.email as string | undefined,
                  role: (bypassMemberData.role as string | undefined) || "user",
                });
              }
            }
          } catch (error) {
            // If we can't check, don't add bypass-user either
            // Only add it if it actually exists in Firebase
          }
          
          setWorkspaceMembers(members);
          setMembersLoaded(true);
          
          // Update cache
          const currentCache = workspaceDataCache.get(workspaceId);
          // Get ownerId from workspace data - it was set earlier in this function
          let cachedOwnerId = currentCache?.workspaceOwnerId ?? null;
          // If we have ownerId from workspaceSnap, use it
          if (workspaceSnap.exists()) {
            const workspaceData = workspaceSnap.data();
            const ownerIdFromWorkspace = workspaceData.ownerId as string | undefined;
            if (ownerIdFromWorkspace) {
              cachedOwnerId = ownerIdFromWorkspace;
            }
          }
          workspaceDataCache.set(workspaceId, {
            workspaceMembers: members,
            membershipEnsured: currentCache?.membershipEnsured ?? true,
            permissions: currentCache?.permissions ?? null,
            currentUserRole: currentCache?.currentUserRole ?? "user",
            workspaceOwnerId: cachedOwnerId,
            timestamp: Date.now(),
          });
          
          // Notify parent component
          if (onMembersLoaded) {
            onMembersLoaded(members);
          }
        } catch (error) {
          console.error("Error loading workspace owner in bypass auth mode:", error);
          // Don't add fake members - only use what actually exists
          // If we can't load, just set empty array (owner will be added when we can access it)
          const emptyMembers: WorkspaceMember[] = [];
          setWorkspaceMembers(emptyMembers);
          setMembersLoaded(true);
          // Update cache
          const currentCache = workspaceDataCache.get(workspaceId);
          workspaceDataCache.set(workspaceId, {
            workspaceMembers: emptyMembers,
            membershipEnsured: currentCache?.membershipEnsured ?? true,
            permissions: currentCache?.permissions ?? null,
            currentUserRole: currentCache?.currentUserRole ?? "user",
            workspaceOwnerId: currentCache?.workspaceOwnerId ?? null,
            timestamp: Date.now(),
          });
          if (onMembersLoaded) {
            onMembersLoaded(emptyMembers);
          }
        }
      };
      
      loadWorkspaceOwner();
      return;
    }
    
    if (!currentUser) return;

    // Initialize with current user immediately so button appears right away
    // This prevents layout shift and makes the button appear with other filters
    const initialUser: WorkspaceMember = {
      id: currentUser.uid,
      name: currentUser.displayName || undefined,
      email: currentUser.email || undefined,
      role: undefined, // Will be set when members are loaded
      photoURL: currentUser.photoURL || undefined,
    };
    const initialMembers = [initialUser];
    setWorkspaceMembers(initialMembers);
    setMembersLoaded(true); // Mark as loaded so button appears immediately
    
    // Update cache with initial user
    const currentCache = workspaceDataCache.get(workspaceId);
    if (!currentCache) {
      workspaceDataCache.set(workspaceId, {
        workspaceMembers: initialMembers,
        membershipEnsured: false,
        permissions: null,
        currentUserRole: "user",
        workspaceOwnerId: null,
        timestamp: Date.now(),
      });
    }
    
    // Initialize with empty array - will be set to all users when members load

    // Helper function to process and add user to state
    const processedUserIds = new Set<string>();
    const addUserToState = (user: WorkspaceMember | null) => {
      if (!user || processedUserIds.has(user.id)) return;
      processedUserIds.add(user.id);
      
      setWorkspaceMembers((prev) => {
        // Avoid duplicates
        if (prev.find((u) => u.id === user.id)) return prev;
        return [...prev, user];
      });
    };

    // Set up real-time listeners for workspace and members
    const workspaceRef = doc(db, "workspaces", workspaceId);
    const membersRef = collection(db, "workspaces", workspaceId, "members");
    
    // Set up real-time listener for workspace document (to get owner)
    const unsubscribeWorkspace = onSnapshot(workspaceRef, (workspaceDoc) => {
      // Owner changes will trigger members listener update
    });

    // Set up real-time listener for members collection
    const unsubscribeMembers = onSnapshot(membersRef, async (membersSnapshot) => {
      try {
        // Get current owner ID from workspace document
        const currentWorkspaceDoc = await getDoc(workspaceRef);
        const currentOwnerId = currentWorkspaceDoc.exists() 
          ? currentWorkspaceDoc.data()?.ownerId as string | undefined 
          : undefined;
        
        // Process members from snapshot
        const memberDataMap = new Map<string, { name?: string; displayName?: string; email?: string; role?: string }>();
        const allUserIds = new Set<string>();
        
        // Add owner if exists
        if (currentOwnerId && currentOwnerId !== workspaceId) {
          allUserIds.add(currentOwnerId);
          memberDataMap.set(currentOwnerId, { role: "owner" });
        }
        
        // Process members collection
        membersSnapshot.docs.forEach((docSnap) => {
          const memberUserId = docSnap.id;
          // Filter out dev-user-developer - we use bypass-user now
          if (memberUserId === "dev-user-developer") {
            console.log(`[Activity] Skipping dev-user-developer (replaced with bypass-user): ${memberUserId}`);
            return;
          }
          if (memberUserId && memberUserId !== workspaceId) {
            allUserIds.add(memberUserId);
            
            const memberData = docSnap.data();
            const memberInfo = {
              name: memberData.name as string | undefined,
              displayName: memberData.displayName as string | undefined,
              email: memberData.email as string | undefined,
              role: (memberData.role as string | undefined) || "user",
            };
            memberDataMap.set(memberUserId, memberInfo);
          }
        });
        
        // Always ensure current user is included
        if (currentUser && currentUser.uid && currentUser.uid !== workspaceId) {
          allUserIds.add(currentUser.uid);
        }
        
        // Validate: ensure workspace ID is not in the set
        if (allUserIds.has(workspaceId)) {
          allUserIds.delete(workspaceId);
        }
        
        // Fetch user details for each user
        const validUserIds = Array.from(allUserIds).filter(userId => 
          userId && 
          userId !== workspaceId && 
          userId !== "dev-user-developer" // Filter out dev-user-developer - we use bypass-user now
        );
        
        const userPromises = validUserIds.map(async (userId: string): Promise<WorkspaceMember | null> => {
          if (userId === workspaceId) return null;
          
          // Skip dev-user-developer - we use bypass-user now
          if (userId === "dev-user-developer") {
            console.log(`[Activity] Skipping dev-user-developer (replaced with bypass-user): ${userId}`);
            return null;
          }
          
          const memberInfo = memberDataMap.get(userId);
          let role: string | undefined = memberInfo?.role;
          // Prefer displayName from members subcollection, then name, then email
          let memberName: string | undefined = memberInfo?.displayName || memberInfo?.name;
          let memberEmail: string | undefined = memberInfo?.email;
          
          // Set role for owner
          if (userId === currentOwnerId) {
            role = "owner";
          } else if (!role) {
            role = "user";
          }
          
          // Try to fetch user doc for photoURL (may fail due to permissions)
          let userDocResult = null;
          try {
            userDocResult = await getDoc(doc(db, "users", userId));
          } catch (userError: any) {
            // Permission errors are expected for other users, ignore them
            if (!userError?.code?.includes("permission")) {
              console.error(`[Activity] Error fetching user doc for ${userId}:`, userError);
            }
            userDocResult = null;
          }
          
          let user: WorkspaceMember | null = null;
          
          if (userDocResult && userDocResult.exists()) {
            const userData = userDocResult.data();
            user = { 
              id: userId, 
              name: (userData.name as string | undefined) || (userData.displayName as string | undefined) || memberName,
              email: (userData.email as string | undefined) || memberEmail,
              role: role || "user",
              photoURL: userData.photoURL as string | undefined,
            };
          } else {
            user = { 
              id: userId, 
              name: memberName,
              email: memberEmail,
              role: role || "user",
              photoURL: undefined,
            };
          }
          
          addUserToState(user);
          return user;
        });

        let usersList = (await Promise.all(userPromises)).filter((user): user is WorkspaceMember => user !== null);
        
        // Filter out orphaned user IDs (starting with "Ao7fhBrt")
        const isOrphanedUserId = (userId: string): boolean => userId.startsWith("Ao7fhBrt");
        usersList = usersList.filter(user => !isOrphanedUserId(user.id));
        
        // Filter out users without proper name/email (orphaned members)
        // These are users that exist in workspaceMembers but have no meaningful data
        // Exception: Always include owner and bypass-user (they're special cases)
        usersList = usersList.filter(user => {
          // Always include owner and bypass-user
          if (user.id === currentOwnerId || user.id === "bypass-user") {
            return true;
          }
          // For other users, they must have either name or email
          return !!(user.name || user.email);
        });
        
        // Ensure current user is in the list
        const currentUserId = currentUser.uid;
        if (!usersList.find((u) => u.id === currentUserId)) {
          const currentUserDoc = await getDoc(doc(db, "users", currentUserId));
          let role: string | undefined = undefined;
          if (currentUserId === currentOwnerId) {
            role = "owner";
          } else {
            const memberDoc = await getDoc(doc(db, "workspaces", workspaceId, "members", currentUserId));
            if (memberDoc.exists()) {
              const memberData = memberDoc.data();
              role = memberData.role as string | undefined;
            }
          }
          
          let currentUserMember: WorkspaceMember;
          if (currentUserDoc.exists()) {
            const userData = currentUserDoc.data();
            currentUserMember = {
              id: currentUserId,
              name: (userData.name as string | undefined) || (userData.displayName as string | undefined) || currentUser.displayName || undefined,
              email: userData.email as string | undefined || currentUser.email || undefined,
              role: role || "user",
              photoURL: userData.photoURL as string | undefined || currentUser.photoURL || undefined,
            };
          } else {
            currentUserMember = {
              id: currentUserId,
              name: currentUser.displayName || undefined,
              email: currentUser.email || undefined,
              role: role || "user",
              photoURL: currentUser.photoURL || undefined,
            };
          }
          
          // Only add current user if they have name/email (unless they're owner or bypass-user)
          const isCurrentUserOrphaned = currentUserId !== currentOwnerId && 
                                        currentUserId !== "bypass-user" && 
                                        !currentUserMember.name && 
                                        !currentUserMember.email;
          
          if (!isCurrentUserOrphaned) {
            usersList.push(currentUserMember);
            addUserToState(currentUserMember);
          }
        }
        
        // Final safety check: remove any orphaned users that might have slipped through
        usersList = usersList.filter(user => {
          // Always include owner and bypass-user
          if (user.id === currentOwnerId || user.id === "bypass-user") {
            return true;
          }
          // Filter out orphaned IDs
          if (isOrphanedUserId(user.id)) {
            return false;
          }
          // For other users, they must have either name or email
          return !!(user.name || user.email);
        });
        
        // Final update with complete list
        setWorkspaceMembers(usersList);
        setMembersLoaded(true);
        
        // Update cache
        const currentCache = workspaceDataCache.get(workspaceId);
        workspaceDataCache.set(workspaceId, {
          workspaceMembers: usersList,
          membershipEnsured: currentCache?.membershipEnsured ?? true,
          permissions: currentCache?.permissions ?? null,
          currentUserRole: currentCache?.currentUserRole ?? "user",
          workspaceOwnerId: currentCache?.workspaceOwnerId ?? currentOwnerId ?? null,
          timestamp: Date.now(),
        });

        // Notify parent component about loaded members
        if (onMembersLoaded) {
          // Update selected users only if not already set from initial filters
          if (!initialFilters?.users) {
            if (usersList.length > 0) {
              onMembersLoaded(usersList);
            } else {
              onMembersLoaded([]);
            }
          }
        }
      } catch (error) {
        console.error("Error processing workspace members:", error);
        setMembersLoaded(true);
        // Update cache even on error
        const currentCache = workspaceDataCache.get(workspaceId);
        if (currentCache) {
          workspaceDataCache.set(workspaceId, {
            ...currentCache,
            timestamp: Date.now(),
          });
        }
      }
    }, (error) => {
      console.error("Error in members snapshot:", error);
      setMembersLoaded(true);
      // Update cache even on error
      const currentCache = workspaceDataCache.get(workspaceId);
      if (currentCache) {
        workspaceDataCache.set(workspaceId, {
          ...currentCache,
          timestamp: Date.now(),
        });
      }
    });

    return () => {
      unsubscribeWorkspace();
      unsubscribeMembers();
    };
  }, [workspaceId, initialFilters?.users, onMembersLoaded]);

  // Safety net: Always filter out orphaned user IDs and users without name/email whenever workspaceMembers changes
  const prevMembersRef = useRef<string>(""); // Track previous members as JSON string to compare content, not reference
  useEffect(() => {
    const isOrphanedUserId = (userId: string): boolean => userId.startsWith("Ao7fhBrt");
    const hasOrphaned = workspaceMembers.some(user => 
      isOrphanedUserId(user.id) || 
      (user.id !== "bypass-user" && user.id !== workspaceOwnerId && !user.name && !user.email)
    );
    
    // Create a stable string representation of current members for comparison
    const currentMembersKey = JSON.stringify(workspaceMembers.map(u => ({ id: u.id, name: u.name, email: u.email })));
    
    // Skip if we've already processed this exact set of members
    if (prevMembersRef.current === currentMembersKey) {
      return;
    }
    
    if (hasOrphaned) {
      const filtered = workspaceMembers.filter(user => {
        // Always include owner and bypass-user
        if (user.id === workspaceOwnerId || user.id === "bypass-user") {
          return true;
        }
        // Filter out orphaned IDs
        if (isOrphanedUserId(user.id)) {
          return false;
        }
        // For other users, they must have either name or email
        return !!(user.name || user.email);
      });
      
        if (filtered.length !== workspaceMembers.length) {
          const removedIds = workspaceMembers
            .filter(u => !filtered.includes(u))
            .map(u => u.id);
          if (process.env.NODE_ENV === "development") {
            console.warn(`[Activity] Safety net: Found orphaned user(s) in workspaceMembers, removing:`, removedIds);
          }
          prevMembersRef.current = JSON.stringify(filtered.map(u => ({ id: u.id, name: u.name, email: u.email })));
          setWorkspaceMembers(filtered);
          // Update cache
          const currentCache = workspaceDataCache.get(workspaceId);
          if (currentCache) {
            workspaceDataCache.set(workspaceId, {
              ...currentCache,
              workspaceMembers: filtered,
              timestamp: Date.now(),
            });
          }
        } else {
        // Update ref even if no filtering happened to prevent re-running
        prevMembersRef.current = currentMembersKey;
      }
    } else {
      // Update ref to current state to prevent re-running
      prevMembersRef.current = currentMembersKey;
    }
  }, [workspaceMembers, workspaceOwnerId]);

  // Load workspace created date (for period filter "All time") - runs even when auth is null (demo/bypass)
  useEffect(() => {
    if (!workspaceId) return;
    const workspaceRef = doc(db, "workspaces", workspaceId);
    const unsub = onSnapshot(workspaceRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.createdAt) {
          let createdAtDate: Date;
          const ca = data.createdAt;
          if (typeof (ca as Timestamp).toDate === "function") {
            createdAtDate = (ca as Timestamp).toDate();
          } else if (ca instanceof Date) {
            createdAtDate = ca;
          } else if (typeof ca === "number") {
            createdAtDate = new Date(ca);
          } else if (ca && typeof ca === "object" && "seconds" in ca) {
            createdAtDate = new Date((ca as { seconds: number }).seconds * 1000);
          } else {
            createdAtDate = new Date();
          }
          setWorkspaceCreatedAt(createdAtDate);
        }
      }
    });
    return () => unsub();
  }, [workspaceId]);

  return {
    workspaceMembers,
    membersLoaded,
    membershipEnsured,
    permissions,
    currentUserRole,
    workspaceOwnerId,
    workspaceCreatedAt,
  };
};
